<script setup>
import { ref, reactive, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import LoadingOverlay from "@/shared/ui/custom/components/LoadingOverlay.vue";
import BaseTabs from "@/shared/ui/custom/tabs/BaseTabs.vue";
import ConstructorMain from "./tabs/ConstructorMain.vue";
import ConstructorParticipant from "./tabs/ConstructorParticipant.vue";
import ConstructorDatabase from "./tabs/ConstructorDatabase.vue";
import ConstructorAtribut from "./tabs/ConstructorAtribut.vue";
import ConstructorTemplate from "./tabs/ConstructorTemplate.vue";
import Button from "@/shared/ui/button/Button.vue";
import {
  createTemplatesForm,
  getTemplateItem,
  updateTemplateForm,
  getTemplateFieldList,
  createServiceChain,
  getServiceListChains,
} from "@/entities/template";
import ConstructorAttachments from "@/pages/admin/ConstructorActions/tabs/ConstructorAttachments.vue";
import ConstructorHistory from "./tabs/ConstructorHistory.vue";
import ConstructorWorkflow from "./tabs/ConstructorWorkflow.vue";
import { getDirectoryItem } from "@/entities/directory";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

defineProps({
  references: {
    type: Array,
  },
});

function getDefaultFields() {
  return {
    number: {
      name: "№ нотариального действия",
      name_kz: "Нотариаттық іс-қимыл №",
      value: "",
      favourites: true,
      type: "string",
      enums: [],
      example: "",
      nullable: true,
      code: "number",
      key: "number",
    },
    collected_amount: {
      name: "Взыскано",
      name_kz: "Өндірілді",
      value: "",
      favourites: true,
      type: "string",
      enums: [],
      example: "",
      nullable: true,
      code: "collected_amount",
      key: "collected_amount",
    },
    expiration_date: {
      name: "Дата окончания",
      name_kz: "Аяқталу күні",
      value: "",
      favourites: true,
      type: "string",
      enums: [],
      example: "",
      nullable: true,
      code: "expiration_date",
      key: "expiration_date",
    },
    day: {
      name: "Текущий день",
      name_kz: "Ағымдағы күн",
      value: "",
      favourites: true,
      type: "string",
      enums: [],
      example: "",
      nullable: true,
      code: "day",
      key: "day",
    },
  };
}

const loading = ref(false);
const loadingSave = ref(false);
const loadingUpload = ref(false);
const fileInputRef = ref(null);
const isInitializing = ref(false);
const isSwitchingTab = ref(false);
const activeTab = ref("main");
const activeForm = ref(null);
const referenceByCode = ref(null);
const chains = ref([]);

const form = reactive({
  code: null,
  category_code: null,
  description: "",
  description_kz: "",
  description_en: "",
  target_table: "",
  user_uuid: null,
  status: "DRAFT",
  content: { additionalProp1: {} },
  content_kz: { additionalProp1: {} },
  content_en: { additionalProp1: {} },
  languages: [],
  document_fl_type_codes: [],
  document_ul_type_codes: [],
  participant_fl_type_codes: [],
  participant_ul_type_codes: [],
  fields: getDefaultFields(),
  default_participant_code: "",
  show_end_date: false,
  end_date_units: [],
});

const uuid = computed(() => route.params.id);

const templateView = computed(
  () => route.name === "constructor_action_item_view"
);

const isNew = computed(() => uuid.value === "new" || !uuid.value);

const mainValid = computed(() => !!form.code && !!form.category_code);

const tabs = computed(() => {
  const result = {
    main: {
      label: t("Основные параметры"),
      icon: "huge-text-config",
    },
    participant: { label: t("Участники"), icon: "huge-users" },
    database: { label: t("Запросы в БД"), icon: "huge-database" },
    base_template: {
      label: t("Базовый шаблон"),
      icon: "huge-note",
    },
    atribut: {
      label: t("Атрибут"),
      icon: "huge-add-pen",
      show_new: false,
    },
    workflow: { label: t("Правила переходов"), icon: "huge-branch" },
    attachments: { label: t("Вложения"), icon: "huge-file-attach" },
  };

  if (isNew.value) {
    delete result.database;
    delete result.workflow;

    const lockedTooltip = t("Заполните основные параметры");
    for (const key of Object.keys(result)) {
      if (key === "main") continue;
      result[key] = {
        ...result[key],
        disabled: !mainValid.value,
        tooltip: lockedTooltip,
      };
    }
  }

  if (templateView.value) {
    result.history = {
      label: t("История"),
      icon: "huge-history",
    };
  }

  return result;
});

const currentTabComponent = computed(() => {
  const map = {
    main: ConstructorMain,
    participant: ConstructorParticipant,
    database: ConstructorDatabase,
    base_template: ConstructorTemplate,
    atribut: ConstructorAtribut,
    workflow: ConstructorWorkflow,
    attachments: ConstructorAttachments,
    history: templateView.value ? ConstructorHistory : null,
  };

  return map[activeTab.value] || ConstructorMain;
});

async function init() {
  if (isInitializing.value) return;

  isInitializing.value = true;
  loading.value = true;

  try {
    await loadDirectory();
    await loadTemplate();
    await loadTemplateFields();
    await ensureServiceChain();
  } catch (e) {
    console.error("Init error:", e);
  } finally {
    loading.value = false;
    isInitializing.value = false;
  }
}

async function loadDirectory() {
  const data = await getDirectoryItem(uuid.value);

  if (data?.success && data.payload) {
    referenceByCode.value = data.payload;
  }
}

async function loadTemplate() {
  const data = await getTemplateItem(uuid.value);

  if (data?.success && data.payload) {
    Object.assign(form, {
      ...data.payload,
      default_participant_code:
        data.payload.default_participant_code ||
        form.default_participant_code ||
        "",
      languages: data.payload.languages || [],
      show_end_date: !!data.payload.show_end_date,
      end_date_units: data.payload.end_date_units || [],
    });
  }
}

async function loadTemplateFields() {
  await getTemplateFieldList();
}

async function ensureServiceChain() {
  if (!referenceByCode.value) return;

  const params = {
    template_uuid: form?.uuid,
    code: uuid.value,
  };

  const data = await getServiceListChains(params);

  if (data?.success) {
    chains.value = data.payload || [];

    if (!chains.value.length) {
      await doCreateServiceChain();
    }
  }
}

async function doCreateServiceChain() {
  if (!referenceByCode.value) return;

  const params = {
    code: form.code,
    name: referenceByCode.value.name || referenceByCode.value.name_kz || "",
    template_uuid: form.uuid,
  };

  await createServiceChain(params);
}

async function submitForm() {
  const formRef = activeForm.value;

  if (!formRef?.validate) return;

  const valid = await formRef.validate();

  if (!valid) return;

  if (uuid.value && uuid.value !== "new") {
    await updateReference();
  } else {
    await createReference({ targetTab: "participant" });
  }
}

async function createReference({ targetTab = "participant" } = {}) {
  loadingSave.value = true;

  try {
    const data = await createTemplatesForm(form);

    if (!data?.success) return null;

    const payload = data.payload;
    const code = payload?.code;

    if (code) {
      try {
        const dirData = await getDirectoryItem(code);
        if (dirData?.success && dirData?.payload) {
          referenceByCode.value = dirData.payload;
          Object.assign(form, payload);
          await doCreateServiceChain();
        }
      } catch (e) {
        console.error("Create service chain error:", e);
      }
    }

    router.push({
      name: "constructor_action_item",
      params: { id: code },
      query: { tab: targetTab },
    });

    return code;
  } finally {
    loadingSave.value = false;
  }
}

async function updateReference() {
  loadingSave.value = true;

  try {
    const data = await updateTemplateForm(uuid.value, form);

    if (data?.success) {
      router.push({
        name: "constructor_action_item_view",
        params: { id: uuid.value },
      });
    }
  } finally {
    loadingSave.value = false;
  }
}

async function changeTab(tab) {
  if (tab === activeTab.value) return;

  if (isNew.value && tab !== "main") {
    if (loadingSave.value || isSwitchingTab.value) return;

    isSwitchingTab.value = true;

    try {
      const formRef = activeForm.value;
      const valid = formRef?.validate ? await formRef.validate() : false;

      if (!valid) {
        activeTab.value = "main";
        router.replace({ query: { ...route.query, tab: "main" } });
        return;
      }

      await createReference({ targetTab: tab });
    } finally {
      isSwitchingTab.value = false;
    }
    return;
  }

  router.replace({
    query: { ...route.query, tab },
  });
}

function triggerTemplateUpload() {
  fileInputRef.value?.click();
}

async function onTemplateFileSelected(e) {
  const file = e.target.files?.[0];
  e.target.value = "";
  if (!file || activeTab.value !== "base_template") return;

  loadingUpload.value = true;
  try {
    await activeForm.value?.loadDocxFile?.(file);
  } finally {
    loadingUpload.value = false;
  }
}

function goToEdit() {
  router.push({
    name: "constructor_action_item",
    params: { id: uuid.value },
    query: { tab: activeTab.value },
  });
}

function goToView() {
  router.push({
    name: "constructor_action_item_view",
    params: { id: uuid.value },
    query: { tab: activeTab.value },
  });
}

watch(
  uuid,
  async (newVal) => {
    if (newVal && newVal !== "new") {
      await init();
    }
  },
  { immediate: true }
);

watch(
  () => route.query.tab,
  (tab) => {
    activeTab.value = tab || "main";
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6 z-1000 w-full h-full">
    <loading-overlay v-if="loading" :value="loading" />
    <div v-else class="flex w-full items-start justify-center rounded-3xl">
      <BaseTabs :tabs="tabs" :active-tab="activeTab" @change:tab="changeTab">
        <template #action>
          <div v-if="!templateView" class="flex items-center gap-2">
            <el-tooltip :content="$t('Режим просмотра')" placement="top">
              <Button variant="outline" size="icon" @click="goToView">
                <svg-icon name="huge-eye" class="w-4 h-4 text-foreground" />
              </Button>
            </el-tooltip>
            <input
              ref="fileInputRef"
              type="file"
              accept=".doc,.docx"
              class="hidden"
              @change="onTemplateFileSelected"
            />
            <Button
              v-if="activeTab === 'base_template'"
              variant="outline"
              @click="triggerTemplateUpload"
              :disabled="loading || loadingSave || loadingUpload"
            >
              <span
                v-if="loadingUpload"
                class="h-4 w-4 rounded-full border-2 border-t-transparent animate-spin"
              ></span>
              <svg-icon
                v-else
                name="huge-file-attach"
                class="w-4 h-4 text-foreground"
              />
              {{ $t("Загрузить шаблон") }}
            </Button>
            <Button
              variant="default"
              @click="submitForm"
              :disabled="loading || loadingSave"
            >
              <span
                v-if="loadingSave"
                class="h-4 w-4 rounded-full border-2 border-t-transparent animate-spin"
              ></span>
              {{ $t("Сохранить") }}
            </Button>
          </div>
          <div v-else class="flex items-center gap-3">
            <Button variant="outline" @click="goToEdit">
              <svg-icon name="huge-edit" class="w-4 h-4 text-foreground" />
              {{ $t("Редактировать") }}
            </Button>
          </div>
        </template>
      </BaseTabs>
    </div>

    <component
      :is="currentTabComponent"
      :form="form"
      ref="activeForm"
      class="flex-1 w-full h-full"
    />
  </div>
</template>

<style scoped lang="scss">
.button {
  @apply h-10 flex justify-center items-center p-4 bg-card border;
  @apply border-border rounded outline-none;
  &:active {
    transform: scale(0.98);
  }
}

.menu {
  @apply w-[282px] flex flex-col p-2 gap-2 bg-card rounded-xl outline outline-1 outline-offset-[-1px];
}

.item {
  @apply flex items-center px-3 py-2 gap-3 rounded-lg cursor-pointer transition-colors;
  svg {
    @apply text-foreground;
    &.danger {
      @apply text-red-400;
    }
  }

  span {
    @apply text-base font-medium leading-6 text-foreground;
    &.danger {
      @apply text-red-400;
    }
  }
}
</style>
